import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, ArrowRight, Check, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import StrataMap from '@/components/StrataMap';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const industries = [
  'Technology', 'Financial Services', 'Healthcare', 'Manufacturing', 
  'Consulting', 'Consumer Goods', 'Energy', 'Telecommunications', 'Other'
];

const regions = [
  'North America', 'Europe', 'Asia Pacific', 'Middle East', 
  'Africa', 'Latin America', 'Global'
];

const instrumentTypes = [
  { category: 'psychometric', instruments: ['MBTI', 'DISC', 'Hogan HPI', 'Hogan HDS', 'Big Five', 'EQi-2.0', 'CliftonStrengths', 'viaEdge', 'SHL OPQ32'] },
  { category: '360_feedback', instruments: ['Korn Ferry 360', 'DDI 360', 'CCL 360', 'Hogan 360', 'Custom 360'] },
  { category: 'performance_review', instruments: ['Annual Review', 'Mid-Year Review', 'Development Plan'] },
  { category: 'upward_survey', instruments: ['Engagement Survey', 'Team Pulse', 'Upward Feedback'] },
  { category: 'linkedin', instruments: ['LinkedIn Profile Export'] },
];

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    current_role: user?.current_role || '',
    industry: user?.industry || '',
    region: user?.region || ''
  });
  const [uploadData, setUploadData] = useState({
    file: null,
    category: '',
    instrument_type: ''
  });
  const [mapScores, setMapScores] = useState({
    ground: 0, observe: 0, connect: 0, execute: 0, integrate: 0
  });

  const handleIdentitySubmit = async () => {
    if (!formData.current_role) {
      toast.error('Please enter your current role');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update profile');

      const updatedUser = await response.json();
      updateUser(updatedUser);
      setStep(2);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadData.file || !uploadData.category || !uploadData.instrument_type) {
      toast.error('Please select a file, category, and instrument type');
      return;
    }

    setLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', uploadData.file);
      formDataObj.append('category', uploadData.category);
      formDataObj.append('instrument_type', uploadData.instrument_type);

      const response = await fetch(`${API_URL}/api/sources/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formDataObj
      });

      if (!response.ok) throw new Error('Failed to upload file');

      const source = await response.json();
      toast.success('Document uploaded successfully!');

      // Animate map petal brightening
      const categoryStateMapping = {
        psychometric: 'ground',
        '360_feedback': 'observe',
        upward_survey: 'connect',
        performance_review: 'execute',
        linkedin: 'integrate'
      };
      
      const primaryState = categoryStateMapping[uploadData.category] || 'ground';
      setMapScores(prev => ({
        ...prev,
        [primaryState]: Math.min(prev[primaryState] + 30, 100)
      }));

      setStep(3);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ onboarding_complete: true })
      });

      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableInstruments = () => {
    const categoryData = instrumentTypes.find(c => c.category === uploadData.category);
    return categoryData?.instruments || [];
  };

  return (
    <div className="min-h-screen bg-obsidian flex">
      {/* Left panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Progress indicators */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`flex-1 h-1 rounded-full transition-all duration-500 ${
                  s <= step ? 'bg-amber-600' : 'bg-slate-mid'
                }`}
              />
            ))}
          </div>

          {/* Step 1: Identity */}
          {step === 1 && (
            <div className="animate-fade-in" data-testid="onboarding-step-1">
              <h1 className="font-display text-3xl text-white mb-2">Welcome to Strata Record</h1>
              <p className="text-slate-400 mb-8">Let's start by learning a bit about you.</p>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="role" className="text-slate-300 mb-2 block">Current Role / Title</Label>
                  <Input
                    id="role"
                    value={formData.current_role}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_role: e.target.value }))}
                    placeholder="e.g., VP of Engineering"
                    className="strata-input"
                    data-testid="input-role"
                  />
                </div>

                <div>
                  <Label htmlFor="industry" className="text-slate-300 mb-2 block">Industry</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
                  >
                    <SelectTrigger className="strata-input" data-testid="select-industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate border-slate-mid">
                      {industries.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="region" className="text-slate-300 mb-2 block">Region</Label>
                  <Select
                    value={formData.region}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}
                  >
                    <SelectTrigger className="strata-input" data-testid="select-region">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate border-slate-mid">
                      {regions.map(reg => (
                        <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleIdentitySubmit}
                  disabled={loading}
                  className="w-full strata-button-primary py-6"
                  data-testid="continue-btn"
                >
                  {loading ? 'Saving...' : 'Continue'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: First Upload */}
          {step === 2 && (
            <div className="animate-fade-in" data-testid="onboarding-step-2">
              <h1 className="font-display text-3xl text-white mb-2">Add Your First Layer</h1>
              <p className="text-slate-400 mb-8">Upload an assessment, feedback report, or other leadership document.</p>

              <div className="space-y-6">
                <div>
                  <Label className="text-slate-300 mb-2 block">Document Type</Label>
                  <Select
                    value={uploadData.category}
                    onValueChange={(value) => setUploadData(prev => ({ ...prev, category: value, instrument_type: '' }))}
                  >
                    <SelectTrigger className="strata-input" data-testid="select-category">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate border-slate-mid">
                      <SelectItem value="psychometric">Psychometric Assessment</SelectItem>
                      <SelectItem value="360_feedback">360 Feedback Report</SelectItem>
                      <SelectItem value="performance_review">Performance Review</SelectItem>
                      <SelectItem value="upward_survey">Upward / Engagement Survey</SelectItem>
                      <SelectItem value="linkedin">LinkedIn Export</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {uploadData.category && (
                  <div className="animate-fade-in">
                    <Label className="text-slate-300 mb-2 block">Instrument / Source</Label>
                    <Select
                      value={uploadData.instrument_type}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, instrument_type: value }))}
                    >
                      <SelectTrigger className="strata-input" data-testid="select-instrument">
                        <SelectValue placeholder="Select instrument" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate border-slate-mid">
                        {getAvailableInstruments().map(inst => (
                          <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                        ))}
                        <SelectItem value="Other">Other (Expert Review)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-slate-300 mb-2 block">Upload Document</Label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                      uploadData.file ? 'border-amber-600/50 bg-amber-600/5' : 'border-slate-mid hover:border-slate-600'
                    }`}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.txt"
                      onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                      data-testid="file-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {uploadData.file ? (
                        <div className="flex items-center justify-center gap-3">
                          <Check className="w-6 h-6 text-amber-500" />
                          <span className="text-white">{uploadData.file.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 mx-auto text-slate-500 mb-3" />
                          <p className="text-slate-400">Drag and drop or click to upload</p>
                          <p className="text-slate-500 text-sm mt-1">PDF, DOCX, JPG, PNG</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(3)}
                    className="flex-1 strata-button-secondary py-6"
                  >
                    Skip for Now
                  </Button>
                  <Button 
                    onClick={handleFileUpload}
                    disabled={loading || !uploadData.file}
                    className="flex-1 strata-button-primary py-6"
                    data-testid="upload-btn"
                  >
                    {loading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Map Tour */}
          {step === 3 && (
            <div className="animate-fade-in" data-testid="onboarding-step-3">
              <h1 className="font-display text-3xl text-white mb-2">The Strata Record Leadership Framework</h1>
              <p className="text-slate-400 mb-8">
                The Strata Record Leadership Framework maps your leadership across five states — Ground, Observe, Connect, Execute, and Integrate. Think of them not as personality types, but as strata you move between. Great leaders don't live in one layer. They access whichever state the moment demands — fluidly, deliberately, without being stratum-locked.
              </p>

              <div className="strata-card p-6 mb-8">
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { state: 'Ground', color: '#2E6B4F', desc: 'Identity, values, stability' },
                    { state: 'Observe', color: '#1A4A6B', desc: 'Self-awareness, blind spots' },
                    { state: 'Connect', color: '#7B3F6E', desc: 'Relational style, influence' },
                    { state: 'Execute', color: '#8B3A1A', desc: 'Cognitive strength, drive' },
                    { state: 'Integrate', color: '#3D5C2A', desc: 'Synthesis, wisdom' },
                  ].map(({ state, color, desc }) => (
                    <div key={state} className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />
                      <div>
                        <span className="text-white font-medium">{state}</span>
                        <span className="text-slate-500 text-sm ml-2">— {desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-slate-500 text-sm mb-6">
                Your Strata Record reveals which layers are rich, which are growing, and how freely you move between them.
              </p>

              <Button 
                onClick={() => setStep(4)}
                className="w-full strata-button-primary py-6"
                data-testid="next-step-btn"
              >
                I Understand
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 4 && (
            <div className="animate-fade-in" data-testid="onboarding-step-4">
              <h1 className="font-display text-3xl text-white mb-2">Build Your Strata Record</h1>
              <p className="text-slate-400 mb-8">
                Add more sources to unlock powerful synthesis. You need at least 3 sources for Strata Record Synthesis.
              </p>

              <div className="strata-card p-6 mb-8">
                <h3 className="text-white font-medium mb-4">Next Steps</h3>
                <ul className="space-y-3 text-slate-400">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-600/20 text-amber-500 flex items-center justify-center text-sm shrink-0 mt-0.5">1</div>
                    <span>Upload 2 more leadership documents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-mid text-slate-400 flex items-center justify-center text-sm shrink-0 mt-0.5">2</div>
                    <span>Purchase Strata Record Synthesis to unlock insights</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-mid text-slate-400 flex items-center justify-center text-sm shrink-0 mt-0.5">3</div>
                    <span>Create your Proof document for opportunities</span>
                  </li>
                </ul>
              </div>

              <Button 
                onClick={completeOnboarding}
                disabled={loading}
                className="w-full strata-button-primary py-6"
                data-testid="complete-onboarding-btn"
              >
                {loading ? 'Loading...' : 'Go to Dashboard'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Right panel - Map visualization */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 border-l border-slate-mid/50">
        <div className="flex justify-center">
          <StrataMap scores={mapScores} size={450} animated={true} />
        </div>
      </div>
    </div>
  );
}
